/**
 * What a reviewer needs to see about one family, in their words rather than the table's.
 *
 * The rows come back with the parent's current details, what TASS held before them, which fields
 * differ, whether any of their students changed, and how many card images were uploaded. This
 * turns that into something readable: the point of the screen is deciding whether a change is
 * right, which needs the old value next to the new one.
 */

export const CONTACT_FIELD_LABELS = {
    addr1: 'Address line 1',
    addr2: 'Address line 2',
    addr3: 'Address line 3',
    town_sub: 'Town / suburb',
    state_code: 'State',
    post_code: 'Postcode',
    mobile1: 'Mother mobile',
    mobile2: 'Father mobile',
    e_mail: 'Email',
    e_mail2: 'Second email',
};

const text = (value) => (value == null ? '' : String(value).trim());

/** One line per field the parent changed: what it was, and what it is now. */
export const changedContactRows = (row = {}) =>
    (row.changed_fields || []).map((field) => ({
        field,
        label: CONTACT_FIELD_LABELS[field] || field,
        was: text(row.previous?.[field]) || '—',
        now: text(row[field]) || '—',
    }));

/** Why this family is in the list at all. */
export const changeSummary = (row = {}) => {
    // Entered by hand: nothing the family changed, but the signed form is what needs approving.
    if (row.manual_entry) return 'Manual entry — signed form uploaded';

    const reasons = [];

    const contact = (row.changed_fields || []).length;
    if (contact) {
        reasons.push(`${contact} contact ${contact === 1 ? 'detail' : 'details'}`);
    }

    if (row.student_details_changed) {
        reasons.push('student details');
    }

    const medicare = Number(row.medicare_image_count) || 0;
    const hcc = Number(row.hcc_image_count) || 0;
    if (medicare) reasons.push(`${medicare} Medicare ${medicare === 1 ? 'image' : 'images'}`);
    if (hcc) reasons.push(`${hcc} Health Care Card ${hcc === 1 ? 'image' : 'images'}`);

    // Listed with nothing to show means the baseline is missing, so it is flagged for a person
    // rather than passed over.
    return reasons.length ? reasons.join(', ') : 'No baseline to compare — needs checking';
};

export const STATUS_LABELS = {
    pending: 'Needs review',
    approved: 'Approved',
    synced: 'Sent to TASS',
};

export const statusLabel = (status) => STATUS_LABELS[String(status || '').toLowerCase()] || 'Needs review';

export const isApproved = (row = {}) => {
    const status = String(row.parent_record_status || '').toLowerCase();
    return status === 'approved' || status === 'synced';
};

/** "Approved by E100 on 23 Sep 2026", when it has been. */
export const approvalNote = (row = {}) => {
    const by = text(row.parent_record_approved_by);
    if (!by) return '';

    const at = row.parent_record_approved_at ? new Date(row.parent_record_approved_at) : null;
    const when = at && !Number.isNaN(at.getTime())
        ? at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
        : '';

    return when ? `Approved by ${by} on ${when}` : `Approved by ${by}`;
};

/**
 * Whether the family actually finished, rather than being part-way through.
 *
 * "All re-enrolments" shows families who have started and not finished, which is the point of
 * having that view — but the approval queue must not, and neither must the Approve button. A
 * family still in progress can go back and change the same fields again, so there is nothing
 * settled to approve, and the server refuses it.
 */
export const isFinished = (row = {}) => {
    const status = String(row.sync?.reenrolment || '').trim().toLowerCase();
    return status === 'completed' || status === 'synced';
};

/** "1h 12m", "18m", or '' when either end of it is unknown. */
export const durationLabel = (minutes) => {
    if (minutes == null || !Number.isFinite(Number(minutes))) return '';

    const total = Math.max(0, Math.round(Number(minutes)));
    if (total < 60) return `${total}m`;

    const hours = Math.floor(total / 60);
    const rest = total % 60;

    // Past a day the hours stop meaning anything to read — a family who left it overnight is
    // better described in days than in "31h 40m".
    if (hours >= 24) {
        const days = Math.floor(hours / 24);
        const spareHours = hours % 24;
        return spareHours ? `${days}d ${spareHours}h` : `${days}d`;
    }

    return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/** "28 Sep 2026, 9:05 am", or '' when it was never recorded. */
export const momentLabel = (value) => {
    if (!value) return '';
    const at = new Date(value);
    if (Number.isNaN(at.getTime())) return '';

    return at.toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};
